import React from "react";
import * as Yup from "yup";
import { Facebook, Instagram, LinkedIn, Twitter } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, Link, Typography } from "@mui/material";
import PkCommonInfo from "./PkCommonInfo";
import PkCommonResponse from "./PkCommonResponse";
import { postSubmit } from "../../fetch/postSubmit";

const getInputSx = {
  "& .MuiInputBase-root": {
    p: 0,
    border: 2,
    borderColor: (theme) => theme.palette.grey[600],
    borderRadius: "4px",
    "&:not(.Mui-disabled)": {
      backgroundColor: "white",
      color: (theme) => theme.palette.grey[800],
    },
    "& input::placeholder, & textarea::placeholder": {
      color: (theme) => theme.palette.grey[700],
    },
  },
};
const Summary = () => (
  <Box>
    <img
      src="https://website-img.polycaik.com/w/brand/brand-v1-h.svg"
      alt="logo"
      height={30}
    />
    <Typography
      sx={{ mt: 3, fontWeight: 400, lineHeight: "2" }}
      variant="subtitle2"
      fontFamily="Lato-Regular"
    >
      Level the playing field with Polycaik — an innovative software solution
      designed to help content creators build interactive digital experiences —{" "}
      <strong>on their own</strong>.
    </Typography>
  </Box>
);
const Connect = () => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ mt: 2, ml: "auto", display: "flex" }}>
        {((sx = { m: 1, p: 1 }) => (
          <>
            {[
              {
                __icon: <Instagram />,
                __link: "https://www.instagram.com/polycaik/",
              },
              {
                __icon: <Facebook />,
                __link: "https://www.facebook.com/polycaik/",
              },
              {
                __icon: <Twitter />,
                __link: "https://www.twitter.com/polycaik/",
              },
              {
                __icon: <LinkedIn />,
                __link: "https://www.linkedin.com/company/polycaik/",
              },
            ].map((ic, i) => (
              <IconButton
                key={`${ic.__link}:icon:${i}`}
                sx={{
                  ...sx,
                  backgroundColor: (theme) => theme.palette.grey[800],
                  "&:hover": {
                    color: "primary.main",
                  },
                }}
                onClick={() => {
                  window.open(ic.__link, "_blank");
                }}
              >
                {ic.__icon}
              </IconButton>
            ))}
          </>
        ))()}
      </Box>
    </Box>
  );
};
const Inquiry = () => {
  const [viewer, viewerSet] = React.useState(<></>);
  const [status, statusSet] = React.useState({ submit: false });
  return (
    <Box>
      <Typography sx={{ mb: 1.6 }} variant="subtitle2">
        GOT ANY QUESTIONS?
      </Typography>
      <Box sx={{ display: "flex", mb: 1.5 }}>
        <Box sx={{ width: "100%", mr: 0.5, ...getInputSx }}>
          <PkCommonInfo
            disabled={status.submit}
            validate={(v) =>
              Yup.string().min(2).required("Required").validateSync(v)
            }
            update={(v) => statusSet({ ...status, fname: v })}
            format={(v) => v}
            value=""
            label=""
            name="name"
            placeholder="NAME"
          />
        </Box>
        <Box sx={{ width: "100%", ml: 0.5, ...getInputSx }}>
          <PkCommonInfo
            disabled={status.submit}
            validate={(v) =>
              Yup.string().email().required("Required").validateSync(v)
            }
            update={(v) => statusSet({ ...status, email: v })}
            format={(v) => v}
            value=""
            label=""
            name="email"
            placeholder="EMAIL"
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          ...getInputSx,
        }}
      >
        <PkCommonInfo
          disabled={status.submit}
          multiline={true}
          validate={(v) =>
            Yup.string().min(10).required("Required").validateSync(v)
          }
          update={(v) => statusSet({ ...status, range: v })}
          format={(v) => v}
          value=""
          label=""
          name="info"
          placeholder="YOUR QUESTION HERE..."
        />
        <Button
          disabled={status.submit}
          sx={{ mt: 1.25, width: "40%" }}
          fullWidth
          variant="contained"
          onClick={() => {
            statusSet({ ...status, submit: true });
            postSubmit(
              {
                type: "INQUIRY",
                fname: status.fname,
                email: status.email,
                range: status.range,
              },
              (status) => {
                if (status !== 200) {
                  statusSet({ ...status, submit: false });
                  viewerSet(
                    <PkCommonResponse
                      type="error"
                      response="Please fill in all required fields. If the error persists, refresh the page or reach out to request@polycaik.com."
                    />,
                  );
                  return;
                }
                viewerSet(
                  <PkCommonResponse response="Thank you for inquiry. We'll be in touch shortly." />,
                );
              },
            );
          }}
        >
          SUBMIT
        </Button>
      </Box>
      {viewer}
    </Box>
  );
};
const getSubpage = (l, hasBackground = true, hasColor = true) => (
  <Grid item xs={6} sm={"auto"} key={`${l.title}`}>
    <Link
      sx={{
        p: 1,
        borderRadius: "4px",
        backgroundColor: hasBackground
          ? (theme) => `${theme.palette.grey[800]}66`
          : "transparent",
        "&:hover": {
          backgroundColor: (theme) => `${theme.palette.grey[900]}66`,
          color: "primary.main",
        },
        ...(hasColor
          ? {}
          : {
              color: (theme) => theme.palette.grey[400],
            }),
      }}
      href={l.http}
      variant="subtitle2"
      underline="none"
      fontFamily="Lato-Bold"
    >
      {l.title}
    </Link>
  </Grid>
);
export function PkCommonFooter(props) {
  return (
    <Box
      sx={{
        p: "6rem 0 2rem",
        backgroundColor: (theme) => `#2b2b2b`,
      }}
    >
      <Grid container spacing={6} sx={{ px: `6%` }}>
        <Grid item xs={12} sm={12} lg={5}>
          {Summary()}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {Connect()}
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          {Inquiry()}
        </Grid>
      </Grid>
      <Box
        sx={{ my: 4, borderTop: 1, borderColor: `background.default` }}
      ></Box>
      <Grid
        container
        spacing={6}
        sx={{ px: `6%`, justifyContent: `space-between` }}
      >
        <Grid item xs={12} md={`auto`}>
          <Grid
            sx={{ justifyContent: "flex-start" }}
            container
            rowSpacing={4}
            columnSpacing={2}
          >
            {LINK.map((l) => getSubpage(l))}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={`auto`}>
          <Typography variant="subtitle2" fontSize="small">
            {`© ${new Date().getFullYear()} POLYCAIK. ALL RIGHTS RESERVED.`}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
const LINK = [
  { http: "/privacy", title: "PRIVACY" },
  { http: "/terms", title: "TERMS" },
  { http: "/company", title: "COMPANY" },
  { http: "/contributors", title: "CONTRIBUTORS" },
  { http: "/developers", title: "DEVELOPERS" },
  { http: "/faq", title: "FAQ" },
  { http: "https://blog.polycaik.com", title: "BLOG" },
];
